import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { css } from 'styled-components';

import { Currency } from '__generated__/globalTypes';
import Coin from 'atoms/icons/Coin';
import { Eth } from 'atoms/icons/Eth';
import ScarcityIcon from 'atoms/icons/ScarcityIcon';
import { ConditionalWrapper } from 'atoms/layout/ConditionalWrapper';
import { Horizontal } from 'atoms/layout/flex';
import OpenItemDialogLink from 'components/link/OpenItemDialogLink';
import { useCurrentUserContext } from 'contexts/currentUser';
import useAmountWithConversion from 'hooks/useAmountWithConversion';
import { zeroMonetaryAmount } from 'hooks/useMonetaryAmount';
import { isCardQuality } from 'lib/cards';
import { monetaryAmountFragment } from 'lib/monetaryAmount';
import { qualityNames } from 'lib/players';
import { ScarcityType } from 'lib/scarcity';

import { ActualReward_so5Reward } from './__generated__/index.graphql';

const rewardWrapperStyle = css`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

const BundleImg = styled.img`
  width: var(--double-unit);
`;
const RewardWrapper = styled.div`
  ${rewardWrapperStyle}
`;
const CardRewardWrapper = styled.div<{ withoutLink: boolean }>`
  ${rewardWrapperStyle}
  padding: var(--half-unit);
  border-radius: var(--half-unit);
  &:hover,
  &:focus {
    ${({ withoutLink }) =>
      !withoutLink &&
      css`
        background: var(--c-neutral-100);
      `}
  }
`;

type Props = {
  reward: ActualReward_so5Reward;
  count: number;
  withoutLink?: boolean;
};

export const ActualReward = ({ reward, count, withoutLink }: Props) => {
  const { rewardCards, aasmState, amount, coinAmount, probabilisticBundle } =
    reward;
  const isClaimed = aasmState === 'claimed';
  const { currency } = useCurrentUserContext();
  const { main } = useAmountWithConversion({
    monetaryAmount: amount || zeroMonetaryAmount,
  });
  return (
    <Horizontal>
      {amount && (
        <RewardWrapper as="div">
          {currency === Currency.ETH ? (
            <>
              <Eth />
              {main}
            </>
          ) : (
            main
          )}
        </RewardWrapper>
      )}
      {!!coinAmount && (
        <RewardWrapper>
          <Coin />
          <FormattedNumber value={coinAmount} />
        </RewardWrapper>
      )}
      {probabilisticBundle && (
        <RewardWrapper>
          <BundleImg src={probabilisticBundle.config.pictureUrl} alt="" />
          {count > 1 && `${count} x `}
          {probabilisticBundle.config.name}
        </RewardWrapper>
      )}
      {rewardCards.filter(Boolean).map(({ quality, anyCard: card }) => {
        const upperCaseQuality = quality?.toUpperCase();

        return isClaimed && card ? (
          <ConditionalWrapper
            key={card?.slug}
            wrap={!withoutLink}
            Wrapper={OpenItemDialogLink}
            props={{
              item: card,
              sport: card.sport,
            }}
          >
            <CardRewardWrapper withoutLink={!!withoutLink}>
              <ScarcityIcon scarcity={card.rarityTyped} />
              {card.anyPlayer.displayName}
            </CardRewardWrapper>
          </ConditionalWrapper>
        ) : (
          <RewardWrapper key={card?.slug}>
            <ScarcityIcon scarcity={card?.rarityTyped as ScarcityType} />
            {isCardQuality(upperCaseQuality) && (
              <FormattedMessage
                id="ActualReward.unclaimedCard"
                defaultMessage="Unclaimed {quality}"
                values={{
                  quality: qualityNames[upperCaseQuality],
                }}
              />
            )}
          </RewardWrapper>
        );
      })}
    </Horizontal>
  );
};

ActualReward.fragments = {
  so5Reward: gql`
    fragment ActualReward_so5Reward on So5Reward {
      slug
      amount {
        ...MonetaryAmountFragment_monetaryAmount
      }
      coinAmount
      aasmState
      probabilisticBundle {
        id
        config {
          id
          name
          pictureUrl
        }
      }
      rewardCards {
        id
        quality
        anyCard {
          slug
          rarityTyped
          sport
          anyPlayer {
            slug
            displayName
          }
        }
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<ActualReward_so5Reward>,
};
